import React, { useState, useEffect, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Activity,
  RefreshCw,
  ChevronDown,
  CheckCircle,
  XCircle,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow, format } from "date-fns";

const STATUS_CONFIG = {
  sent: { label: "Sent", color: "info", icon: Send },
  acked: { label: "Acknowledged", color: "success", icon: CheckCircle },
  failed: { label: "Failed", color: "error", icon: XCircle },
};

export default function LogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "all",
  });
  const [expandedLogs, setExpandedLogs] = useState(new Set());

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status && filters.status !== "all") {
        params.append("status", filters.status);
      }

      const response = await fetch(`${API}/dispatch-logs?${params}`, {
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to fetch logs");

      const data = await response.json();
      setLogs(data.logs);
    } catch (error) {
      toast.error("Failed to load dispatch logs");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filters.status]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const toggleExpand = (logId) => {
    setExpandedLogs((prev) => {
      const next = new Set(prev);
      if (next.has(logId)) {
        next.delete(logId);
      } else {
        next.add(logId);
      }
      return next;
    });
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.sent;
    const Icon = config.icon;

    return (
      <Badge
        variant="outline"
        className={`status-badge-${config.color} flex items-center gap-1`}
      >
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "PPpp");
    } catch {
      return dateString;
    }
  };

  const formatJson = (obj) => {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  return (
    <Layout
      title="Dispatch Logs"
      actions={
        <Button
          onClick={fetchLogs}
          variant="outline"
          size="sm"
          data-testid="refresh-logs-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      }
    >
      {/* Filters */}
      <Card className="card-surface mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Select
              value={filters.status}
              onValueChange={(value) =>
                setFilters((f) => ({ ...f, status: value }))
              }
            >
              <SelectTrigger className="w-[180px]" data-testid="log-status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="acked">Acknowledged</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="card-surface">
        {loading ? (
          <CardContent className="p-6">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="skeleton-row" />
            ))}
          </CardContent>
        ) : logs.length === 0 ? (
          <CardContent className="py-16 text-center">
            <Activity className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">No dispatch logs yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Logs will appear here when events are dispatched
            </p>
          </CardContent>
        ) : (
          <div className="divide-y">
            {logs.map((log) => (
              <Collapsible
                key={log.id}
                open={expandedLogs.has(log.id)}
                onOpenChange={() => toggleExpand(log.id)}
              >
                <CollapsibleTrigger asChild>
                  <div
                    className="flex items-center justify-between p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                    data-testid={`log-row-${log.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <ChevronDown
                        className={`w-4 h-4 text-muted-foreground transition-transform ${
                          expandedLogs.has(log.id) ? "rotate-180" : ""
                        }`}
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(log.status)}
                          <Badge variant="outline">{log.target_system}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1 font-mono truncate max-w-[400px]">
                          {log.destination_url}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">
                        {formatDistanceToNow(new Date(log.created_at), {
                          addSuffix: true,
                        })}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {log.attempts} attempt{log.attempts !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <div className="px-4 pb-4 pt-0 ml-8 space-y-4">
                    {/* Details */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="overline mb-1">Log ID</p>
                        <p className="font-mono text-xs">{log.id}</p>
                      </div>
                      <div>
                        <p className="overline mb-1">Item ID</p>
                        <p className="font-mono text-xs">{log.inbound_item_id}</p>
                      </div>
                      <div>
                        <p className="overline mb-1">Created</p>
                        <p className="text-xs">{formatDate(log.created_at)}</p>
                      </div>
                      <div>
                        <p className="overline mb-1">Updated</p>
                        <p className="text-xs">{formatDate(log.updated_at)}</p>
                      </div>
                    </div>

                    {/* Error */}
                    {log.last_error && (
                      <div>
                        <p className="overline mb-1">Error</p>
                        <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 font-mono">
                          {log.last_error}
                        </div>
                      </div>
                    )}

                    {/* Request Payload */}
                    {log.request_payload && (
                      <div>
                        <p className="overline mb-1">Request Payload</p>
                        <pre className="json-viewer text-xs max-h-[300px] overflow-auto">
                          {formatJson(log.request_payload)}
                        </pre>
                      </div>
                    )}

                    {/* Response */}
                    {log.response_data && (
                      <div>
                        <p className="overline mb-1">Response</p>
                        <pre className="json-viewer text-xs">
                          {formatJson(log.response_data)}
                        </pre>
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
        )}
      </Card>
    </Layout>
  );
}
